import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { apiFetch } from "../../utils/api";
import "./AdminSocialLinks.css";

/* ─────────────────────────────────────
   Known platform presets
   ───────────────────────────────────── */
const PLATFORM_PRESETS = [
  { label: "Facebook",   icon: "fab fa-facebook-f",   color: "#1877f2" },
  { label: "Instagram",  icon: "fab fa-instagram",     color: "#e1306c" },
  { label: "Twitter / X",icon: "fab fa-x-twitter",     color: "#000000" },
  { label: "YouTube",    icon: "fab fa-youtube",        color: "#ff0000" },
  { label: "LinkedIn",   icon: "fab fa-linkedin-in",    color: "#0a66c2" },
  { label: "TikTok",     icon: "fab fa-tiktok",         color: "#010101" },
  { label: "WhatsApp",   icon: "fab fa-whatsapp",       color: "#25d366" },
  { label: "Pinterest",  icon: "fab fa-pinterest-p",    color: "#e60023" },
  { label: "Snapchat",   icon: "fab fa-snapchat",       color: "#fffc00" },
  { label: "Telegram",   icon: "fab fa-telegram-plane", color: "#229ed9" },
  { label: "Custom",     icon: "",                       color: "#6b7280" },
];

const EMPTY_FORM = {
  platform:       "",
  url:            "",
  icon_class:     "",
  show_in_nav:    0,
  show_in_footer: 1,
  show_in_contact:0,
  active:         1,
  order:          0,
};

/* ─────────────────────────────────────
   Platform Icon preview
   ───────────────────────────────────── */
function PlatformIcon({ iconClass, platform, size = 16 }) {
  const preset = PLATFORM_PRESETS.find(
    (p) => p.label.toLowerCase() === (platform || "").toLowerCase()
  );
  const color = preset?.color || "#6b7280";

  if (iconClass) {
    return (
      <i
        className={iconClass}
        style={{ fontSize: size, color, width: size, textAlign: "center" }}
      />
    );
  }
  return (
    <span style={{ fontSize: size, color, lineHeight: 1 }}>🔗</span>
  );
}

/* ─────────────────────────────────────
   Placement chips
   ───────────────────────────────────── */
function PlacementChips({ item }) {
  const chips = [
    { key: "show_in_nav",     label: "Nav"     },
    { key: "show_in_footer",  label: "Footer"  },
    { key: "show_in_contact", label: "Contact" },
  ].filter((c) => Number(item[c.key]) === 1);

  if (!chips.length) return <span className="asl-no-placement">—</span>;
  return (
    <div className="asl-chips">
      {chips.map((c) => (
        <span key={c.key} className="asl-chip">{c.label}</span>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────
   Status Pill
   ───────────────────────────────────── */
function StatusPill({ active }) {
  return (
    <span
      className={`asl-status-pill ${
        Number(active) === 1 ? "asl-status-pill--active" : "asl-status-pill--inactive"
      }`}
    >
      {Number(active) === 1 ? "● Active" : "○ Inactive"}
    </span>
  );
}

/* ─────────────────────────────────────
   Dot Menu
   ───────────────────────────────────── */
function DotMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="asl-dot-menu" ref={ref}>
      <button
        className="asl-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="asl-dot-menu__dropdown">
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="asl-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="asl-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="asl-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════
   ADD / EDIT MODAL
   ═══════════════════════════════════ */
function SocialModal({ item, onClose, onSaved }) {
  const isEdit = !!item?.id;
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    if (item) {
      setForm({
        platform:        item.platform        || "",
        url:             item.url             || "",
        icon_class:      item.icon_class      || "",
        show_in_nav:     Number(item.show_in_nav)     || 0,
        show_in_footer:  Number(item.show_in_footer)  ?? 1,
        show_in_contact: Number(item.show_in_contact) || 0,
        active:          Number(item.active) === 1 ? 1 : 0,
        order:           item.order ?? 0,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [item]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setError("");
  };

  /* When a platform preset is picked, pre-fill icon_class */
  const handlePreset = (label) => {
    const preset = PLATFORM_PRESETS.find((p) => p.label === label);
    set("platform", label === "Custom" ? "" : label);
    if (preset && preset.icon) set("icon_class", preset.icon);
  };

  const handleSave = async () => {
    setError("");
    if (!form.platform.trim()) return setError("Platform name is required.");
    if (!form.url.trim())      return setError("URL is required.");
    if (!form.icon_class.trim()) return setError("Icon class is required.");

    setSaving(true);
    try {
      const payload = {
        platform:        form.platform.trim(),
        url:             form.url.trim(),
        icon_class:      form.icon_class.trim(),
        show_in_nav:     form.show_in_nav,
        show_in_footer:  form.show_in_footer,
        show_in_contact: form.show_in_contact,
        active:          form.active,
        order:           parseInt(form.order) || 0,
      };

      if (isEdit) {
        await apiFetch(`/api/admin/social-links/${item.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch("/api/admin/social-links", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="asl-modal-backdrop" onClick={handleBackdrop}>
      <div className="asl-modal">

        {/* Header */}
        <div className="asl-modal__header">
          <div className="asl-modal__title-row">
            <span className="asl-modal__tag">{isEdit ? "EDIT" : "NEW"}</span>
            <h2 className="asl-modal__title">
              {isEdit ? `Edit — ${item.platform}` : "Add Social Link"}
            </h2>
          </div>
          <button className="asl-modal__close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="asl-modal__body">
          {error && (
            <div className="asl-alert asl-alert--error">⚠ {error}</div>
          )}

          {/* ── Quick-pick presets ── */}
          <div className="asl-msec">
            <h4 className="asl-msec__title">Quick Pick</h4>
            <div className="asl-presets">
              {PLATFORM_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  className={`asl-preset-btn ${form.platform === p.label ? "asl-preset-btn--active" : ""}`}
                  onClick={() => handlePreset(p.label)}
                  title={p.label}
                >
                  {p.icon
                    ? <i className={p.icon} style={{ color: p.color }} />
                    : <span style={{ fontSize: 13 }}>🔗</span>}
                  <span>{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Platform Details ── */}
          <div className="asl-msec">
            <h4 className="asl-msec__title">Platform Details</h4>
            <div className="asl-two-col">
              <div className="asl-field">
                <label className="asl-field__label">
                  Platform Name <span className="asl-req">*</span>
                </label>
                <input
                  className="asl-input"
                  value={form.platform}
                  onChange={(e) => set("platform", e.target.value)}
                  placeholder="e.g. Facebook"
                />
              </div>
              <div className="asl-field">
                <label className="asl-field__label">
                  Icon Class <span className="asl-req">*</span>
                </label>
                <div className="asl-icon-row">
                  <input
                    className="asl-input"
                    value={form.icon_class}
                    onChange={(e) => set("icon_class", e.target.value)}
                    placeholder="fab fa-facebook-f"
                  />
                  {form.icon_class && (
                    <span className="asl-icon-preview">
                      <i className={form.icon_class} />
                    </span>
                  )}
                </div>
                <p className="asl-field__hint">
                  Font Awesome class, e.g. <code>fab fa-instagram</code>
                </p>
              </div>
              <div className="asl-field asl-field--full">
                <label className="asl-field__label">
                  URL <span className="asl-req">*</span>
                </label>
                <input
                  className="asl-input"
                  type="url"
                  value={form.url}
                  onChange={(e) => set("url", e.target.value)}
                  placeholder="https://facebook.com/yourpage"
                />
              </div>
            </div>
          </div>

          {/* ── Display Placement ── */}
          <div className="asl-msec">
            <h4 className="asl-msec__title">Display Placement</h4>
            <p className="asl-msec__sub">
              Choose where this link appears on the site.
            </p>
            <div className="asl-placement-grid">
              {[
                { key: "show_in_nav",     label: "Navigation",    desc: "Top nav bar"       },
                { key: "show_in_footer",  label: "Footer",        desc: "Site footer"       },
                { key: "show_in_contact", label: "Contact Page",  desc: "Contact section"   },
              ].map(({ key, label, desc }) => (
                <label key={key} className="asl-placement-card">
                  <input
                    type="checkbox"
                    checked={Number(form[key]) === 1}
                    onChange={(e) => set(key, e.target.checked ? 1 : 0)}
                  />
                  <div className="asl-placement-card__body">
                    <span className="asl-placement-card__label">{label}</span>
                    <span className="asl-placement-card__desc">{desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* ── Display Settings ── */}
          <div className="asl-msec">
            <h4 className="asl-msec__title">Display Settings</h4>
            <div className="asl-two-col">
              <div className="asl-field">
                <label className="asl-field__label">Display Order</label>
                <p className="asl-field__hint">Lower numbers appear first.</p>
                <input
                  className="asl-input"
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={(e) => set("order", parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="asl-field">
                <label className="asl-field__label">Visibility</label>
                <label className="asl-check">
                  <input
                    type="checkbox"
                    checked={Number(form.active) === 1}
                    onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                  />
                  <span>Active (visible on site)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="asl-modal__footer">
          <div />
          <div className="asl-modal__footer-right">
            <button className="asl-btn asl-btn--cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="asl-btn asl-btn--save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Social Link"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE CONFIRM DIALOG
   ═══════════════════════════════════ */
function DeleteDialog({ item, onCancel, onConfirm, deleting }) {
  return (
    <div
      className="asl-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="asl-confirm">
        <div className="asl-confirm__icon">🗑</div>
        <h3 className="asl-confirm__title">Delete Social Link?</h3>
        <p className="asl-confirm__msg">
          This will permanently remove the{" "}
          <strong>{item?.platform}</strong> social link. This action cannot be undone.
        </p>
        <div className="asl-confirm__actions">
          <button className="asl-btn asl-btn--cancel" onClick={onCancel} disabled={deleting}>
            Cancel
          </button>
          <button className="asl-btn asl-btn--danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? "Deleting..." : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminSocialLinks() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  /* Filters */
  const [search,       setSearch]       = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [filterPlace,  setFilterPlace]  = useState("");
  const [sort,         setSort]         = useState("order_asc");

  /* Modals */
  const [editTarget,   setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  /* ── Load ── */
  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ sort, limit: 200 });
    if (search)              params.set("search", search);
    if (filterActive !== "") params.set("active", filterActive);
    if (filterPlace  !== "") params.set("placement", filterPlace);

    apiFetch(`/api/admin/social-links?${params}`)
      .then((data) => setItems(data.social_links || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, filterActive, filterPlace, sort]);

  useEffect(() => { load(); }, [load]);

  /* ── Stats ── */
  const totalActive   = items.filter((i) => Number(i.active) === 1).length;
  const totalInactive = items.filter((i) => Number(i.active) !== 1).length;

  /* ── Toggle ── */
  const handleToggle = async (item) => {
    setError(""); setSuccess("");
    try {
      const res = await apiFetch(`/api/admin/social-links/${item.id}/toggle`, { method: "PUT" });
      setSuccess(res.message || "Status updated.");
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/admin/social-links/${deleteTarget.id}`, { method: "DELETE" });
      setSuccess(`${deleteTarget.platform} social link deleted.`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleSaved = () => {
    setEditTarget(null);
    setSuccess("Social link saved successfully.");
    load();
  };

  if (loading) {
    return (
      <AdminLayout pageTag="SETTINGS" pageTitle="Social Links">
        <div className="asl-skeleton" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTag="SETTINGS" pageTitle="Social Links">

      {/* ── Intro bar ── */}
      <div className="asl-intro-bar">
        <div className="asl-intro-bar__left">
          <span className="asl-intro-bar__icon">🔗</span>
          <div>
            <h2 className="asl-intro-bar__title">Social Links</h2>
            <p className="asl-intro-bar__sub">
              Manage social media profiles shown across the site.
            </p>
          </div>
        </div>
        <div className="asl-intro-bar__right">
          <span className="asl-stat-pill">
            <span className="asl-stat-pill__num">{totalActive}</span> Active
          </span>
          <span className="asl-stat-pill">
            <span className="asl-stat-pill__num">{totalInactive}</span> Inactive
          </span>
          <span className="asl-stat-pill">
            <span className="asl-stat-pill__num">{items.length}</span> Total
          </span>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="asl-alert asl-alert--error">⚠ {error}</div>}
      {success && <div className="asl-alert asl-alert--success">✓ {success}</div>}

      {/* ── Toolbar ── */}
      <div className="asl-toolbar">
        <div className="asl-toolbar__left">
          <div className="asl-search-wrap">
            <span className="asl-search-icon">⌕</span>
            <input
              className="asl-search"
              type="text"
              placeholder="Search platforms..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="asl-filter-select"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          <select
            className="asl-filter-select"
            value={filterPlace}
            onChange={(e) => setFilterPlace(e.target.value)}
          >
            <option value="">All Placements</option>
            <option value="nav">Nav</option>
            <option value="footer">Footer</option>
            <option value="contact">Contact</option>
          </select>
          <select
            className="asl-filter-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="order_asc">Order ↑</option>
            <option value="order_desc">Order ↓</option>
            <option value="platform_asc">Platform A-Z</option>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
        <button className="asl-btn-add" onClick={() => setEditTarget({})}>
          + Add Social Link
        </button>
      </div>

      {/* ── Table ── */}
      {items.length === 0 ? (
        <div className="asl-empty">
          <div className="asl-empty__icon">🔗</div>
          <h3 className="asl-empty__title">No Social Links Found</h3>
          <p className="asl-empty__sub">
            {search || filterActive !== "" || filterPlace !== ""
              ? "Try adjusting your filters."
              : "Click \"Add Social Link\" to get started."}
          </p>
        </div>
      ) : (
        <div className="asl-table-wrap">
          <table className="asl-table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>URL</th>
                <th>Placement</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className={Number(item.active) !== 1 ? "asl-row--inactive" : ""}>
                  <td>
                    <div className="asl-platform-cell">
                      <span className="asl-platform-cell__icon">
                        <PlatformIcon iconClass={item.icon_class} platform={item.platform} size={18} />
                      </span>
                      <div>
                        <span className="asl-platform-cell__name">{item.platform}</span>
                        {item.icon_class && (
                          <code className="asl-platform-cell__class">{item.icon_class}</code>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="asl-url-link"
                      title={item.url}
                    >
                      {item.url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "").slice(0, 40)}
                      {item.url.length > 50 ? "…" : ""}
                    </a>
                  </td>
                  <td><PlacementChips item={item} /></td>
                  <td>
                    <span className="asl-order-badge">{item.order ?? 0}</span>
                  </td>
                  <td><StatusPill active={item.active} /></td>
                  <td>
                    <div className="asl-actions">
                      <button
                        className={`asl-icon-btn ${
                          Number(item.active) === 1
                            ? "asl-icon-btn--toggle-on"
                            : "asl-icon-btn--toggle-off"
                        }`}
                        title={Number(item.active) === 1 ? "Deactivate" : "Activate"}
                        onClick={() => handleToggle(item)}
                      >
                        {Number(item.active) === 1 ? "✓" : "○"}
                      </button>
                      <DotMenu
                        onEdit={()   => setEditTarget(item)}
                        onDelete={() => setDeleteTarget(item)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal ── */}
      {editTarget !== null && (
        <SocialModal
          item={Object.keys(editTarget).length === 0 ? null : editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
        />
      )}

      {/* ── Delete confirm ── */}
      {deleteTarget && (
        <DeleteDialog
          item={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

    </AdminLayout>
  );
}